/**
 * How Bailey reacts in the style chair. This file is only the timetable and
 * the decision of when to react, so it has no Three.js and is easy to test;
 * studioPoses.js has the poses of the same names.
 *
 * Bailey reacts to a run of changes once you pause, never while you are
 * clicking through swatches, and not every time: each change earns points,
 * and a reaction needs enough points, a pause, a cooldown since the last one
 * and a roll of the dice. The first new cut always gets one, so people learn
 * early that Bailey notices.
 */
export const REACTIONS = {
    hairFlick: { duration: 2.0, lines: ['Ooh, I like this.', 'Now we’re talking.', 'Very me.'] },
    ooh: { duration: 1.9, lines: ['Bold. I’m into it.', 'That’s a statement.', 'Ooh.'] },
    nailCheck: { duration: 2.4, lines: ['Fresh set!', 'Don’t touch, they’re drying.'] },
    showArm: { duration: 2.2, lines: ['No regrets.', 'Tough but friendly.'] },
    teeTug: { duration: 2.0, lines: ['Fits like a dream.', 'Crisp.'] },
    lashFlutter: { duration: 1.8, lines: ['Serving looks.', 'Just a touch.'] },
    wink: { duration: 1.4, lines: ['Looking good.', 'Nice.'] },
    swivel: { duration: 2.2, lines: ['Wheee.', 'Love it.'] },
    indecision: { duration: 2.4, lines: ['Take your time.', 'Both work, honestly.'] },
    breezy: { duration: 2.0, lines: ['Breezy.', 'Back to basics.'] },
    // A quiet beat after a long pause. Never has a line.
    glance: { duration: 2.6, lines: [] }
};
export const REACTION_IDS = Object.keys(REACTIONS);

export const RULES = { pause: 1.4, cooldown: 10, threshold: 4, chance: 0.7, lineChance: 0.5, idleAfter: 25, indecisionWindow: 12 };

// Points per kind of change, and the reactions that suit it, best first.
const KINDS = {
    cut: { points: 3, reactions: ['hairFlick', 'wink'] },
    bald: { points: 3, reactions: ['breezy'] },
    bold: { points: 1.5, reactions: ['ooh', 'hairFlick'] },
    hair: { points: 1, reactions: ['hairFlick', 'wink'] },
    technique: { points: 2, reactions: ['hairFlick', 'ooh'] },
    nails: { points: 1.5, reactions: ['nailCheck'] },
    tattoo: { points: 3, reactions: ['showArm'] },
    tee: { points: 1, reactions: ['teeTug', 'swivel'] },
    makeup: { points: 1.5, reactions: ['lashFlutter', 'wink'] },
    surprise: { points: 4, reactions: ['swivel', 'ooh'] }
};

function hsl(hex) {
    const n = parseInt(String(hex).slice(1), 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return { h: h * 60, s, l };
}
/** A hair colour no one is born with. Warm browns and reds are natural. */
export function isBoldHair(hex) {
    const { h, s, l } = hsl(hex);
    return s > 0.3 && l > 0.2 && l < 0.88 && !(h >= 8 && h <= 45);
}

/** What kind of change this is, from the field and its old and new values. */
export function kindOf(field, from, to) {
    if (field === 'surprise') return 'surprise';
    if (field === 'hairStyle') return to === 'none' || to === 'buzz' ? 'bald' : 'cut';
    if (field === 'hairColour' || field === 'hairAccent') return isBoldHair(to) ? 'bold' : 'hair';
    if (field === 'hairTechnique') return 'technique';
    if (field === 'nailColour' || field === 'nailsEnabled') return 'nails';
    if (field === 'tattooLeft' || field === 'tattooRight' || field === 'tattooInk') return 'tattoo';
    if (field === 'shirtColour') return 'tee';
    return 'makeup';
}

export function createDirector({ random = Math.random } = {}) {
    let points = 0, tally = {}, lastChange = -Infinity, lastReaction = -Infinity;
    let firstCut = true, idleDone = true, last = '', lastLine = '';
    let history = [];
    function pick(candidates) {
        const fresh = candidates.filter(id => id !== last);
        return (fresh.length ? fresh : ['wink', 'swivel'].filter(id => id !== last))[0];
    }
    function line(id) {
        const lines = REACTIONS[id].lines.filter(l => l !== lastLine);
        if (!lines.length || random() >= RULES.lineChance) return null;
        lastLine = lines[Math.floor(random() * lines.length) % lines.length];
        return lastLine;
    }
    // Flipping one thing back and forth: four or more changes to the same
    // field within the window, landing on a value it has had before.
    function undecided(at) {
        history = history.filter(h => at - h.at <= RULES.indecisionWindow);
        const byField = {};
        history.forEach(h => { (byField[h.field] = byField[h.field] || []).push(h.to); });
        return Object.keys(byField).some(field => {
            const values = byField[field];
            return values.length >= 4 && values.slice(0, -1).indexOf(values[values.length - 1]) !== -1;
        });
    }
    function fire(id, at) {
        points = 0; tally = {}; lastReaction = at; last = id; history = [];
        return { id, line: line(id) };
    }
    return {
        /** Record changes: [{ field, from, to }] at `at` seconds. */
        change(changes, at) {
            lastChange = at; idleDone = false;
            changes.forEach(({ field, from, to }) => {
                const kind = kindOf(field, from, to);
                points += KINDS[kind].points;
                tally[kind] = (tally[kind] || 0) + KINDS[kind].points;
                history.push({ field, to, at });
            });
        },
        /** When the next decision is due, in seconds, or null. */
        nextCheck() {
            if (points > 0) return lastChange + RULES.pause;
            if (!idleDone && lastChange > -Infinity) return lastChange + RULES.idleAfter;
            return null;
        },
        /** Decide at `at` seconds: a reaction { id, line } or null. */
        decide(at) {
            // A hair's breadth of slack: timers land on the boundary.
            if (at - lastChange < RULES.pause - 1e-3) return null;
            if (points <= 0) {
                if (!idleDone && at - lastChange >= RULES.idleAfter - 1e-3 && at - lastReaction >= RULES.cooldown) {
                    idleDone = true; lastReaction = at;
                    return { id: 'glance', line: null };
                }
                return null;
            }
            if (firstCut && tally.cut) { firstCut = false; return fire(pick(KINDS.cut.reactions), at); }
            if (at - lastReaction < RULES.cooldown) { points = 0; tally = {}; return null; }
            if (undecided(at)) return fire(pick(['indecision']), at);
            if (points < RULES.threshold) return null;
            if (random() >= RULES.chance) { points = Math.floor(points / 2); tally = {}; return null; }
            const kind = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
            return fire(pick(KINDS[kind].reactions), at);
        },
        /** Saving or leaving: no more reactions until the next change. */
        quiet() { points = 0; tally = {}; idleDone = true; history = []; }
    };
}
